import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Play, 
  Lock, 
  CheckCircle, 
  Clock,
  BookOpen,
  Download,
  Eye,
  ChevronRight,
  AlertCircle,
  Leaf,
  Sun,
  Wind,
  Snowflake,
  Utensils,
  Briefcase,
  Cake,
  Star,
  Flag,
  Zap,
  Archive,
  Coffee,
  Layers,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Video course data - with real YouTube links
const VIDEO_LESSONS = [
  {
    id: 0,
    title_sk: "Úvod do Magickej päťky",
    title_cz: "Úvod do Magické pětky",
    description_sk: "Spoznajte filozofiu varenia, ktorá zmení váš prístup ku každodennému vareniu. Čo je Magická päťka a prečo funguje.",
    description_cz: "Poznejte filozofii vaření, která změní váš přístup k každodennímu vaření. Co je Magická pětka a proč funguje.",
    duration: "10:00",
    youtube_id: "iI2Fub8UxY0",
    thumbnail: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400"
  },
  {
    id: 1,
    title_sk: "CHUTNE – Ako variť tak, aby chutilo celej rodine",
    title_cz: "CHUTNĚ – Jak vařit tak, aby chutnalo celé rodině",
    description_sk: "Prvý pilier Magickej päťky. Tipy a triky, ako pripraviť jedlá, ktoré si zamilujú všetci – od detí po starých rodičov.",
    description_cz: "První pilíř Magické pětky. Tipy a triky, jak připravit jídla, která si zamilují všichni – od dětí po prarodiče.",
    duration: "15:00",
    youtube_id: "97n9zJbYp2c",
    thumbnail: "https://images.unsplash.com/photo-1547592180-85f173990554?w=400"
  },
  {
    id: 2,
    title_sk: "ZDRAVO – Výživné jedlá bez kompromisov",
    title_cz: "ZDRAVĚ – Výživná jídla bez kompromisů",
    description_sk: "Druhý pilier Magickej päťky. Ako variť zdravo bez toho, aby ste sa vzdali chuti. Vyvážené a výživné recepty.",
    description_cz: "Druhý pilíř Magické pětky. Jak vařit zdravě, aniž byste se vzdali chuti. Vyvážené a výživné recepty.",
    duration: "15:00",
    youtube_id: "sG16dv46GTQ",
    thumbnail: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400"
  },
  {
    id: 3,
    title_sk: "RÝCHLO – Jedlá hotové za 30 minút",
    title_cz: "RYCHLE – Jídla hotová za 30 minut",
    description_sk: "Tretí pilier Magickej päťky. Časovo úsporné recepty pre zaneprázdnené dni. Meal prep a organizácia v kuchyni.",
    description_cz: "Třetí pilíř Magické pětky. Časově úsporné recepty pro zaneprázdněné dny. Meal prep a organizace v kuchyni.",
    duration: "15:00",
    youtube_id: "1Is3zFOzOq8",
    thumbnail: "https://images.unsplash.com/photo-1466637574441-749b8f19452f?w=400"
  },
  {
    id: 4,
    title_sk: "DOSTUPNE – Varenie z bežných surovín",
    title_cz: "DOSTUPNĚ – Vaření z běžných surovin",
    description_sk: "Štvrtý pilier Magickej päťky. Ako variť chutne a zdravo z ingrediencií, ktoré nájdete v každom obchode.",
    description_cz: "Čtvrtý pilíř Magické pětky. Jak vařit chutně a zdravě z ingrediencí, které najdete v každém obchodě.",
    duration: "15:00",
    youtube_id: "HWthnYn-9zw",
    thumbnail: "https://images.unsplash.com/photo-1542838132-92c53300491e?w=400"
  },
  {
    id: 5,
    title_sk: "JEDNODUCHO – Recepty, ktoré zvládne každý",
    title_cz: "JEDNODUŠE – Recepty, které zvládne každý",
    description_sk: "Piaty pilier Magickej päťky. Jednoduché postupy bez zbytočných komplikácií. Varenie má byť radosť, nie stres.",
    description_cz: "Pátý pilíř Magické pětky. Jednoduché postupy bez zbytečných komplikací. Vaření má být radost, ne stres.",
    duration: "15:00",
    youtube_id: "pqNLSUa058Q",
    thumbnail: "https://images.unsplash.com/photo-1544025162-d76694265947?w=400"
  }
];

export default function MemberAreaPage({ section = 'all' }) {
  const { user, language, t, loading: authLoading } = useAuth();
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeLesson, setActiveLesson] = useState(null);
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [ebooks, setEbooks] = useState([]);
  const [ebooksLoading, setEbooksLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const checkMembership = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/membership/status`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setMembershipStatus(data);
          if (data.is_active) {
            fetchEbooks();
          }
        }
      } catch (error) {
        console.error('Failed to check membership:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchEbooks = async () => {
      setEbooksLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/ebooks`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setEbooks(data.ebooks || []);
        }
      } catch (err) {
        console.error('Failed to fetch ebooks:', err);
      } finally {
        setEbooksLoading(false);
      }
    };

    checkMembership();

    // Load completed lessons from localStorage
    const saved = localStorage.getItem('completedLessons');
    if (saved) {
      setCompletedLessons(new Set(JSON.parse(saved)));
    }
  }, [user]);

  const markAsCompleted = (lessonId) => {
    const newCompleted = new Set(completedLessons);
    newCompleted.add(lessonId);
    setCompletedLessons(newCompleted);
    localStorage.setItem('completedLessons', JSON.stringify([...newCompleted]));
  };

  const getLocalizedField = (item, field) => {
    if (language === 'cz' && item[`${field}_cz`]) {
      return item[`${field}_cz`];
    }
    return item[`${field}_sk`] || item[field] || '';
  };

  const getEbookIcon = (icon) => {
    const icons = {
      spring: <Leaf className="w-5 h-5 text-green-500" />,
      summer: <Sun className="w-5 h-5 text-yellow-500" />,
      autumn: <Wind className="w-5 h-5 text-orange-500" />,
      winter: <Snowflake className="w-5 h-5 text-blue-400" />,
      breakfast: <Coffee className="w-5 h-5 text-amber-600" />,
      lunch: <Utensils className="w-5 h-5 text-primary" />,
      dessert: <Cake className="w-5 h-5 text-pink-500" />,
      festive: <Star className="w-5 h-5 text-yellow-400" />,
      traditional: <Flag className="w-5 h-5 text-red-500" />,
      express: <Zap className="w-5 h-5 text-yellow-500" />,
      cake: <Cake className="w-5 h-5 text-pink-400" />,
      mealprep: <Archive className="w-5 h-5 text-teal-500" />,
      magic: <Sparkles className="w-5 h-5 text-purple-500" />,
      default: <BookOpen className="w-5 h-5 text-primary" />,
    };
    return icons[icon] || icons.default;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      seasonal: language === 'sk' ? 'Sezónne' : 'Sezónní',
      thematic: language === 'sk' ? 'Tematické' : 'Tematické',
      bonus: 'Bonus',
      special: language === 'sk' ? 'Špeciálne' : 'Speciální',
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      seasonal: 'bg-green-100 text-green-700',
      thematic: 'bg-blue-100 text-blue-700',
      bonus: 'bg-amber-100 text-amber-700',
      special: 'bg-purple-100 text-purple-700',
    };
    return colors[category] || 'bg-muted text-muted-foreground';
  };

  const handleDownload = async (ebook) => {
    setDownloadingId(ebook.ebook_id);
    try {
      const res = await fetch(`${API_URL}/api/ebooks/${ebook.ebook_id}/download`, {
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${ebook.ebook_id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleView = async (ebook) => {
    try {
      // Fetch the PDF with credentials (session cookie) and open as a blob URL
      // so the browser PDF viewer shows it in a new tab.
      const res = await fetch(`${API_URL}/api/ebooks/${ebook.ebook_id}/view`, {
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Revoke after a delay so the new tab can load the blob
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      }
    } catch (err) {
      console.error('View failed:', err);
    }
  };

  const categories = ['all', 'seasonal', 'thematic', 'bonus', 'special'];
  // Apply section filter (set by /magicka-patka and /tematicke-kolekcie routes).
  // - 'magicka-patka'    → only the magicka-patka ebook
  // - 'tematicke-kolekcie' → all other ebooks (sezónne, tematické, bonusy)
  const ebooksForSection =
    section === 'magicka-patka'
      ? ebooks.filter(e => e.ebook_id === 'magicka-patka')
      : section === 'tematicke-kolekcie'
        ? ebooks.filter(e => e.ebook_id !== 'magicka-patka')
        : ebooks;
  const filteredEbooks = activeCategory === 'all'
    ? ebooksForSection
    : ebooksForSection.filter(e => e.category === activeCategory);
  const showVideoCourse = section === 'all' || section === 'magicka-patka';
  const showEbooks = section === 'all' || section === 'tematicke-kolekcie' || section === 'magicka-patka';

  // Auth still initializing (prevents flash of "Lock" screen on direct URL navigation)
  if (authLoading) {
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center" data-testid="member-area-auth-loading">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Lock className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
          <h1 className="font-serif text-2xl font-bold text-text-heading mb-4">
            {language === 'sk' ? 'Členská sekcia' : 'Členská sekce'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {language === 'sk' 
              ? 'Pre prístup k videokurzu a ebookom sa prosím prihláste.'
              : 'Pro přístup k videokurzu a ebookům se prosím přihlaste.'
            }
          </p>
          <Button 
            onClick={() => window.location.href = '/login'}
            className="bg-primary hover:bg-primary-hover text-white rounded-full px-8"
          >
            {t('login')}
          </Button>
        </div>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // No active membership
  if (!membershipStatus?.is_active) {
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-6" />
          <h1 className="font-serif text-2xl font-bold text-text-heading mb-4">
            {language === 'sk' ? 'Členstvo vypršalo' : 'Členství vypršelo'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {language === 'sk' 
              ? 'Vaše členstvo vypršalo. Pre prístup k obsahu si prosím obnovte predplatné.'
              : 'Vaše členství vypršelo. Pro přístup k obsahu si prosím obnovte předplatné.'
            }
          </p>
          <Button 
            className="bg-primary hover:bg-primary-hover text-white rounded-full px-8"
            onClick={() => window.open('https://app.cliqsales.cz', '_blank')}
          >
            {language === 'sk' ? 'Obnoviť členstvo' : 'Obnovit členství'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-subtle py-8" data-testid="member-area-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <span className="px-2 py-1 bg-secondary/20 text-secondary rounded-full text-xs font-medium">
              {membershipStatus.membership_type === 'yearly' 
                ? (language === 'sk' ? 'Ročné členstvo' : 'Roční členství')
                : (language === 'sk' ? 'Mesačné členstvo' : 'Měsíční členství')
              }
            </span>
            <span>•</span>
            <span>
              {language === 'sk' ? 'Zostáva' : 'Zbývá'} {membershipStatus.days_remaining} {language === 'sk' ? 'dní' : 'dní'}
            </span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-text-heading">
            {section === 'magicka-patka'
              ? (language === 'sk' ? 'Magická päťka' : 'Magická pětka')
              : section === 'tematicke-kolekcie'
                ? (language === 'sk' ? 'Tematické kolekcie' : 'Tematické kolekce')
                : (language === 'sk' ? 'Členská sekcia' : 'Členská sekce')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {section === 'magicka-patka'
              ? (language === 'sk'
                  ? 'Kompletný videokurz a sprievodný ebook k filozofii Magickej päťky.'
                  : 'Kompletní videokurz a doprovodný ebook k filozofii Magické pětky.')
              : section === 'tematicke-kolekcie'
                ? (language === 'sk'
                    ? '12 prémiových ebookov — sezónne, tematické a bonusové kolekcie.'
                    : '12 prémiových ebooků — sezónní, tematické a bonusové kolekce.')
                : (language === 'sk'
                    ? 'Vitajte späť! Tu nájdete všetok exkluzívny obsah.'
                    : 'Vítejte zpět! Zde najdete veškerý exkluzivní obsah.')}
          </p>
        </div>

        {/* Video Player (when active) */}
        {activeLesson && (
          <div className="mb-8 bg-white rounded-3xl overflow-hidden shadow-hover">
            <div className="aspect-video bg-black">
              {activeLesson.youtube_id ? (
                <iframe
                  src={`https://www.youtube.com/embed/${activeLesson.youtube_id}?rel=0`}
                  title={getLocalizedField(activeLesson, 'title')}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white">
                  <div className="text-center">
                    <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>{language === 'sk' ? 'Video bude čoskoro dostupné' : 'Video bude brzy dostupné'}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-serif text-xl font-semibold text-text-heading">
                    {language === 'sk' ? 'Lekcia' : 'Lekce'} {activeLesson.id}: {getLocalizedField(activeLesson, 'title')}
                  </h2>
                  <p className="text-muted-foreground mt-2">
                    {getLocalizedField(activeLesson, 'description')}
                  </p>
                </div>
                <Button
                  variant={completedLessons.has(activeLesson.id) ? "secondary" : "outline"}
                  onClick={() => markAsCompleted(activeLesson.id)}
                  className="shrink-0"
                >
                  <CheckCircle className={cn("w-4 h-4 mr-2", completedLessons.has(activeLesson.id) && "text-white")} />
                  {completedLessons.has(activeLesson.id) 
                    ? (language === 'sk' ? 'Dokončené' : 'Dokončeno')
                    : (language === 'sk' ? 'Označiť ako dokončené' : 'Označit jako dokončeno')
                  }
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Video Course */}
          {showVideoCourse && (
          <div className="lg:col-span-3">
            <div className="bg-white rounded-3xl p-6 shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Play className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="font-serif text-xl font-semibold text-text-heading">
                    {language === 'sk' ? 'Videokurz' : 'Videokurz'}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {completedLessons.size} / {VIDEO_LESSONS.length} {language === 'sk' ? 'lekcií dokončených' : 'lekcí dokončeno'}
                  </p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-muted rounded-full h-2 mb-6">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(completedLessons.size / VIDEO_LESSONS.length) * 100}%` }}
                />
              </div>

              {/* Lessons list */}
              <div className="space-y-3">
                {VIDEO_LESSONS.map((lesson) => (
                  <button
                    key={lesson.id}
                    onClick={() => setActiveLesson(lesson)}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 rounded-2xl transition-all text-left",
                      activeLesson?.id === lesson.id
                        ? "bg-primary/10 border-2 border-primary"
                        : "bg-muted/50 hover:bg-muted border-2 border-transparent"
                    )}
                  >
                    {/* Thumbnail */}
                    <div className="relative w-24 h-16 rounded-lg overflow-hidden shrink-0">
                      <img 
                        src={lesson.thumbnail} 
                        alt={getLocalizedField(lesson, 'title')}
                        className="w-full h-full object-cover"
                      />
                      {completedLessons.has(lesson.id) && (
                        <div className="absolute inset-0 bg-secondary/80 flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground mb-1">
                        {language === 'sk' ? 'Lekcia' : 'Lekce'} {lesson.id}
                      </p>
                      <h3 className="font-medium text-text-heading truncate">
                        {getLocalizedField(lesson, 'title')}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>{lesson.duration}</span>
                      </div>
                    </div>

                    <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          )}

          {/* Ebooks Section - Full Width */}
          {showEbooks && (
          <div className="lg:col-span-3 mt-0">
            <div className="bg-white rounded-3xl p-6 shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h2 className="font-serif text-xl font-semibold text-text-heading">
                    {language === 'sk' ? 'Ebooky na stiahnutie' : 'Ebooky ke stažení'}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {ebooks.length} {language === 'sk' ? 'ebookov k dispozícii' : 'ebooků k dispozici'}
                  </p>
                </div>
              </div>

              {/* Category filter */}
              <div className="flex flex-wrap gap-2 mb-6" data-testid="ebook-category-filter">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={cn(
                      "px-4 py-1.5 rounded-full text-sm font-medium transition-all",
                      activeCategory === cat
                        ? "bg-primary text-white"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    )}
                  >
                    {cat === 'all' 
                      ? (language === 'sk' ? 'Všetky' : 'Všechny')
                      : getCategoryLabel(cat)
                    }
                  </button>
                ))}
              </div>

              {ebooksLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="ebooks-grid">
                  {filteredEbooks.map((ebook) => (
                    <div
                      key={ebook.ebook_id}
                      className="bg-white border border-border rounded-2xl overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-all"
                      data-testid={`ebook-card-${ebook.ebook_id}`}
                    >
                      {/* Cover preview */}
                      <div className="aspect-[4/3] bg-muted/40 overflow-hidden">
                        {ebook.cover_url ? (
                          <img
                            src={`${API_URL}${ebook.cover_url}`}
                            alt={language === 'sk' ? ebook.title_sk : ebook.title_cz}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {getEbookIcon(ebook.icon)}
                          </div>
                        )}
                      </div>

                      {/* Body */}
                      <div className="p-4 flex flex-col flex-1">
                        {/* Icon + category */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-10 h-10 rounded-xl bg-muted/40 flex items-center justify-center">
                            {getEbookIcon(ebook.icon)}
                          </div>
                          <span className={cn(
                            "text-xs font-medium px-2 py-0.5 rounded-full",
                            getCategoryColor(ebook.category)
                          )}>
                            {getCategoryLabel(ebook.category)}
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="font-serif text-base font-semibold text-text-heading leading-snug mb-2">
                          {language === 'sk' ? ebook.title_sk : ebook.title_cz}
                        </h3>

                        {/* Description */}
                        <p className="text-xs text-muted-foreground leading-relaxed mb-4 line-clamp-3 flex-1">
                          {language === 'sk' ? ebook.description_sk : ebook.description_cz}
                        </p>

                        {/* File size + actions */}
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {ebook.file_size_display || `${ebook.file_size_mb} MB`} · {ebook.file_format || 'PDF'}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleView(ebook)}
                              disabled={!ebook.available}
                              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-text-heading transition-colors disabled:opacity-50"
                              data-testid={`view-btn-${ebook.ebook_id}`}
                            >
                              <Eye className="w-3.5 h-3.5" />
                              {language === 'sk' ? 'Zobraziť' : 'Zobrazit'}
                            </button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownload(ebook)}
                              disabled={downloadingId === ebook.ebook_id || !ebook.available}
                              className="h-7 text-xs rounded-full border-primary text-primary hover:bg-primary hover:text-white"
                              data-testid={`download-btn-${ebook.ebook_id}`}
                            >
                              {downloadingId === ebook.ebook_id ? (
                                <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
                              ) : (
                                <Download className="w-3 h-3 mr-1" />
                              )}
                              {language === 'sk' ? 'Stiahnuť' : 'Stáhnout'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Membership Info */}
          <div className="lg:col-span-3 mt-6 bg-white rounded-3xl p-6 shadow-soft">
            <h3 className="font-medium text-text-heading mb-3">
              {language === 'sk' ? 'Vaše členstvo' : 'Vaše členství'}
            </h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">{language === 'sk' ? 'Typ' : 'Typ'}: </span>
                <span className="font-medium">
                  {membershipStatus?.membership_type === 'yearly' 
                    ? (language === 'sk' ? 'Ročné' : 'Roční')
                    : (language === 'sk' ? 'Mesačné' : 'Měsíční')
                  }
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">{language === 'sk' ? 'Zostáva' : 'Zbývá'}: </span>
                <span className="font-medium">{membershipStatus?.days_remaining} {language === 'sk' ? 'dní' : 'dní'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
